/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import dayjs from "dayjs";
import { Else, If, Then, When } from "react-if";
import { useHistory, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Col,
  FlexContainer,
  Hidden,
  ModalConfirm,
  Row,
  Spacer,
  theme,
} from "@nordcloud/gnui";
import { useApplicationQuery, useNidEventsQuery } from "~/generated/graphql";
import {
  ACTION,
  BreadcrumbsBox,
  BrickLoader,
  BudgetChart,
  BudgetChartWrapper,
  ErrorWrap,
  LoaderWrap,
  PopoverControls,
} from "~/components";
import {
  CostBox,
  CustomComponents,
  formatCostObject,
} from "~/components/CostBox";
import { formatSavingSuggestions } from "~/components/CostBox/helpers";
import { dateFormat } from "~/constants";
import {
  useCurrency,
  useQueryState,
  useScroll,
  useScrollDisclosure,
  useUuidValidator,
} from "~/hooks";
import { ROUTES } from "~/routing/routes";
import { ACTIONS, Can, useAccess } from "~/services/auth";
import { periodDayJs, useBillingPeriod } from "~/services/customers";
import { generateActionConfirmText, isNotEmpty, isNotNil, noop } from "~/tools";
import { ApplicationCostTabs } from "./ApplicationCostTabs";
import { ApplicationDetailsTabs } from "./ApplicationDetailsTabs";
import { AddEnvironment } from "./components/AddEnvironment";
import { EditableDetails } from "./EditableDetails";
import { useGetBudgetState, useRemoveApplication } from "./hooks";
import { ReadonlyDetails } from "./ReadonlyDetails";
import { RouteParams } from "./types";

const breadcrumbsTags = [
  {
    color: "success",
    text: "Application",
  },
];

export function ApplicationDetailsPage() {
  const [isModalConfirmOpen, setIsModalConfirmOpen] = React.useState(false);

  const scrollToRef = React.useRef<HTMLDivElement>(null);

  const { application: appId } = useParams<RouteParams>();
  useUuidValidator(appId);

  const history = useHistory();

  const { data: eventsData } = useNidEventsQuery({
    variables: {
      nid: appId,
      from: periodDayJs().startOf("month").toISOString(),
      to: periodDayJs().endOf("month").toISOString(),
    },
  });

  const {
    data: {
      application,
      costs,
      progress,
      isForecast,
      savingSuggestions,
      status,
    },
    error,
    loading,
  } = useApplicationData(appId);

  const { removeApplication, isDefaultApplication } = useRemoveApplication({
    id: application.id,
    hasEnvs: isNotEmpty(application.environments),
    name: application.name,
    setIsModalConfirmOpen,
    onSuccess: () => history.replace(ROUTES.applications.index),
  });

  useScroll({
    ref: scrollToRef,
    trigger: () => !loading,
    once: true,
  });

  const { hasAccess: canEdit } = useAccess(ACTIONS.updateApplicationOwner);
  const { isCurrentBillingPeriod } = useBillingPeriod();
  const { set: setCostTab } = useQueryState<{ costTab: "budget" }>();

  const breadcrumbLabels = isNotEmpty(application.name)
    ? [{ value: application.id, label: application.name }]
    : undefined;

  const {
    triggerScroll,
    scrollToRef: scrollToBudgetRef,
    isScrolled,
    setIsScrolled,
  } = useScrollDisclosure();

  useScroll({
    ref: scrollToBudgetRef,
    smooth: true,
    trigger: () => isScrolled,
    onFinish: () => {
      setIsScrolled(false);
      setCostTab("costTab")("budget");
    },
  });

  return (
    <ErrorWrap error={error}>
      <div ref={scrollToRef} />
      <Row>
        <Col>
          <BreadcrumbsBox
            title={application.name ?? application.id}
            tags={breadcrumbsTags}
            labels={breadcrumbLabels}
          >
            <AddEnvironment ownerId={application.owner?.id ?? ""} />
            <Can action={ACTIONS.removeApplication}>
              <When condition={!isDefaultApplication}>
                <PopoverControls
                  trigger={
                    <div
                      data-testid="popover-trigger"
                      css={{
                        svg: {
                          width: "2rem",
                          height: "2rem",
                        },
                      }}
                    >
                      <Button
                        severity="low"
                        icon="menu"
                        size="md"
                        data-testid="popover"
                      />
                    </div>
                  }
                  items={[
                    {
                      label: "Delete application",
                      action: ACTION.DELETE,
                      onClick: () => setIsModalConfirmOpen(true),
                    },
                  ]}
                />
              </When>
            </Can>
          </BreadcrumbsBox>
          <Box spacing="spacing04">
            <Row>
              <Col xs={8} sm={8} md={5} lg={8}>
                <FlexContainer css={{ height: "100%" }}>
                  <LoaderWrap
                    loading={loading}
                    Component={BrickLoader}
                    viewBox="0 0 8 2"
                  >
                    <Hidden xs>
                      <BudgetChartWrapper>
                        <BudgetChart
                          progress={progress}
                          color={status.color}
                          hasBudget={isNotNil(costs.budget) && costs.budget > 0}
                          isForecast={isForecast}
                        />
                      </BudgetChartWrapper>
                    </Hidden>

                    <If condition={isCurrentBillingPeriod}>
                      <Then>
                        <EditableDetails
                          id={application.id}
                          name={application.name ?? application.displayName}
                          owner={application.owner}
                          contacts={application.contacts}
                          description={application.description}
                          readonlyOwner={!canEdit}
                        />
                      </Then>
                      <Else>
                        <ReadonlyDetails
                          name={application.name ?? application.displayName}
                          owner={application.owner.name}
                          contact={application.contacts[0]?.name ?? ""}
                          description={application.description}
                        />
                      </Else>
                    </If>
                  </LoaderWrap>
                </FlexContainer>
              </Col>
              <Col sm={8} md={3} lg={4}>
                <CostBox
                  numericalValues={costs}
                  customComponent={CustomComponents.BUDGET}
                  loading={loading}
                  savingSuggestions={savingSuggestions}
                  customComponentConfig={{
                    scrollToBudget: triggerScroll,
                  }}
                />
              </Col>
            </Row>
          </Box>
          <div ref={scrollToBudgetRef}>
            <ApplicationCostTabs
              costsLoading={loading}
              events={eventsData?.events ?? []}
              forecast={costs.forecast}
              currentMonthCosts={
                application.cost?.currentMonthDaily?.filter(isNotNil) ?? []
              }
            />
          </div>
          <Spacer height={theme.spacing.spacing04} />
          <ApplicationDetailsTabs nid={application.id} />
        </Col>
      </Row>
      <ModalConfirm
        isOpen={isModalConfirmOpen}
        confirm={removeApplication}
        actionLabel="Delete"
        contentLabel="Delete Application"
        onClose={() => setIsModalConfirmOpen(false)}
      >
        {generateActionConfirmText(application.name)("delete")()}
      </ModalConfirm>
    </ErrorWrap>
  );
}

function useApplicationData(id: string) {
  const year = dayjs().format(dateFormat.year);
  const { data, error, loading, refetch } = useApplicationQuery({
    variables: { id, year },
  });

  const application = {
    id: data?.application?.id ?? "",
    name: data?.application?.name ?? "",
    displayName: data?.application?.displayName ?? "",
    description: data?.application?.description ?? "",
    owner: data?.application?.owner ?? {
      id: "",
      name: "",
      email: "",
    },
    contacts: (data?.application?.contacts ?? []).filter(isNotNil),
    environments: data?.application?.environments ?? [],
    cost: data?.application?.cost,
  };

  const { currency } = useCurrency();
  const { isCurrentBillingPeriod } = useBillingPeriod();
  const costs = formatCostObject({
    data: {
      forecast: data?.application?.cost?.forecast,
      budget: data?.application?.budgetYearly.budgetByMonth,
      cloudWasteCost: data?.application?.cloudWasteAggregate?.cost,
      co2e: data?.application?.co2e,
      currentMonth: data?.application?.cost?.currentMonth,
      previousMonth: data?.application?.cost?.previousMonth,
      co2eSavings: data?.application?.cloudWasteAggregate?.co2e,
      reservedInstanceBenefit: "0",
    },
    currency,
    isCurrentBillingPeriod,
  });

  const savingSuggestions = formatSavingSuggestions({
    show: data?.application?.cloudWasteAggregate?.hasWaste,
    type: "Application",
    appId: id,
    clicked: noop,
  });

  const { progress, status, isForecast } = useGetBudgetState({
    forecast: costs.forecast,
    budget: costs.budget,
    cost: costs.currentMonth,
  });

  return {
    data: {
      application,
      costs,
      savingSuggestions,
      progress,
      isForecast,
      status,
    },
    error,
    loading,
    refetch,
  };
}
